// This DataTable is failing for some reason
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Plot from 'react-plotly.js';
import DataTable from 'react-data-table-component';
import './App.css';
import {calculateDaysUntilNextPayment} from './DebtFunctions';
import {formatCurrency, formatRate} from "./CoreFunctions";


const AutoLoanDetail = () => {
    const { id } = useParams();  // Get mortgage ID from the URL
    const [auto_loan, setAutoLoan] = useState(null); // State for mortgage details
    const [loading, setLoading] = useState(true);    // State for loading status
    const [showRemainingOnly, setShowRemainingOnly] = useState(true); // State for toggle
    const [amortizationSchedule, setAmortizationSchedule] = useState([]); // State for amortization schedule

    useEffect(() => {
        const fetchAutoLoanData = async () => {
            try {
                const response = await axios.get(`/api/balance_sheet/financing/auto_loans/${id}/`);
                console.log('API Response:', response.data); // Log the complete response

                setAutoLoan(response.data); // Set mortgage data

                // Convert each entry in amortization_schedule to a proper array format
                const scheduleData = response.data.amortization_schedule.map(entry => ({
                    id: entry.id,
                    payment_date: entry.payment_date,
                    loan_payment_amount: parseFloat(entry.loan_payment_amount), // Convert to float
                    principal_payment_amount: parseFloat(entry.principal_payment_amount),
                    interest_payment_amount: parseFloat(entry.interest_payment_amount),
                    outstanding_balance: parseFloat(entry.outstanding_balance),
                    percent_outstanding: parseFloat(entry.percent_outstanding),
                    cumulative_recurring_payment_amt: parseFloat(entry.cumulative_recurring_payment_amt),
                    cumulative_principal_payment_amt: parseFloat(entry.cumulative_principal_payment_amt),
                    cumulative_interest_payment_amt: parseFloat(entry.cumulative_interest_payment_amt),
                    // Add additional fields if necessary...
                })) || [];

                setAmortizationSchedule(scheduleData); // Set the processed state
                setLoading(false); // Loading complete
            } catch (error) {
                console.error('Error fetching mortgage details:', error);
                setLoading(false);  // Stop loading on error
            }
        };
        fetchAutoLoanData();
    }, [id]);  // Fetch data only when the mortgage ID changes

    // Initialize daysUntilPayment in a safe way
    let daysUntilPayment = null;

    // Check if auto_loan and upcoming_payment are not null before calculating
    if (auto_loan && auto_loan.upcoming_payment) {
        daysUntilPayment = calculateDaysUntilNextPayment(auto_loan.upcoming_payment.payment_date);
    }

    if (loading) {
        return <div>Loading auto loan details...</div>;  // Show loading message
    }

    if (!auto_loan) {
        return <div>No auto loan found!</div>;  // Handle case where no data is found
    }

        // Prepare filtered amortization schedule based on the toggle state
    const filteredSchedule = showRemainingOnly
        ? amortizationSchedule.filter(row => new Date(row.payment_date) > new Date())
        : amortizationSchedule;

    // Prepare data for the Plotly chart
    const xData = filteredSchedule.map(row => new Date(row.payment_date)); // convert to Date object
    const principalPayments = filteredSchedule.map(row => parseFloat(row.principal_payment_amount) || 0); // Use safe access
    const interestPayments = filteredSchedule.map(row => parseFloat(row.interest_payment_amount) || 0); // Use safe access

    const buildAssetUrl = (asset) => {
        if (!asset) return null;
        const id = asset.id;
        const type1 = (asset.type_1 || '').toLowerCase();
        const type2 = (asset.type_2 || '').toLowerCase();
        if (type2 === 'vehicle') return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
        if (type2 === 'resi_property') return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
        return null;
    };

    const columns = [
        { name: 'Payment Date', selector: row => row.payment_date, sortable: true },
        { name: 'Loan Payment Amount', selector: row => `$${parseFloat(row.loan_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Principal Payment', selector: row => `$${parseFloat(row.principal_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Interest Payment', selector: row => `$${parseFloat(row.interest_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Outstanding Balance', selector: row => `$${parseFloat(row.outstanding_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Remaining Outstanding %', selector: row => `${(parseFloat(row.percent_outstanding) * 100).toFixed(1)}%`, sortable: true },
        { name: 'Cumulative Payments', selector: row => `$${parseFloat(row.cumulative_recurring_payment_amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Cumulative Interest Payments', selector: row => `$${parseFloat(row.cumulative_interest_payment_amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
        { name: 'Cumulative Principal Payments', selector: row => `$${parseFloat(row.cumulative_principal_payment_amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, sortable: true },
    ];

    // Check for proper mapping
    console.log('Columns:', columns); // Log the columns
    console.log('First Amortization Entry:', amortizationSchedule[0] || {}); // Log the first entry if available

    // Dark theme styles for DataTable to match app tables
    const dataTableStyles = {
        table: {
            style: {
                backgroundColor: '#111827',
            }
        },
        headRow: {
            style: {
                backgroundColor: '#1f2937',
                color: '#e5e7eb',
                borderBottom: '1px solid #374151',
            }
        },
        headCells: {
            style: {
                color: '#e5e7eb',
            }
        },
        rows: {
            style: {
                backgroundColor: '#111827',
                color: '#e5e7eb',
                borderBottom: '1px solid #374151',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#0b1220',
                color: '#e5e7eb',
            }
        },
        pagination: {
            style: {
                backgroundColor: '#111827',
                color: '#e5e7eb',
                borderTop: '1px solid #374151',
            },
            pageButtonsStyle: {
                fill: '#e5e7eb',
                color: '#e5e7eb',
            }
        },
    };

    const conditionalRowStyles = [
        {
            when: (_row, index) => index % 2 === 1,
            style: {
                backgroundColor: '#0f172a', // subtle alternating row
            },
        },
    ];

return (
    <div>
        <h1>Auto Loan Details: {auto_loan.vehicle_year} {auto_loan.vehicle_make} {auto_loan.vehicle_model}</h1>

                    {/* Container for Stats and Graph */}
            <div className="top-section">
                <div className="loan-stats" style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151', minWidth: '350px' }}>
                    <p><strong>Upcoming Payment: </strong></p>
                    {auto_loan.upcoming_payment ? (
                        <>
                            <p style={{ marginLeft: '30px' }}>Date: {auto_loan.upcoming_payment.payment_date} (in {daysUntilPayment} days)</p>
                            <p style={{ marginLeft: '30px' }}>Amount: {formatCurrency(auto_loan.upcoming_payment.loan_payment_amount)}</p>
                            <p style={{ marginLeft: '30px' }}>Rate: {formatRate(auto_loan.initial_interest_rate / 100, 2)}</p>
                        </>
                    ) : (
                        <p style={{ marginLeft: '30px' }}>No upcoming payment available.</p>
                    )}
                    <p><strong>Loan Stats: </strong></p>
                    {auto_loan.recent_payment ? (
                        <p style={{ marginLeft: '30px' }}>
                            Outstanding: {formatCurrency(auto_loan.recent_payment.outstanding_balance)} ({ (parseFloat(auto_loan.recent_payment.percent_outstanding) * 100).toFixed(1)}%)
                        </p>
                    ) : (
                        <p style={{ marginLeft: '30px' }}>No recent payment data available.</p>
                    )}
                    <p><strong>Asset Link: </strong></p>
                    {auto_loan.asset_info ? (
                        <p style={{ marginLeft: '30px' }}>
                            <a href={buildAssetUrl(auto_loan.asset_info)} style={{ color: '#93c5fd' }}>
                                {auto_loan.asset_info.description || 'Linked Asset'}
                            </a>
                        </p>
                    ) : (
                        <p style={{ marginLeft: '30px' }}>No linked asset found.</p>
                    )}
                </div>

                <div className="plot-container" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>  {/* Container for centering the Plotly graph */}
                    <Plot
                        data={[
                            {
                                x: xData,
                                y: principalPayments,
                                type: 'scatter',
                                mode: 'lines+markers',
                                name: 'Principal Payments',
                            },
                            {
                                x: xData,
                                y: interestPayments,
                                type: 'scatter',
                                mode: 'lines+markers',
                                name: 'Interest Payments',
                            },
                        ]}
                        layout={{
                            template: 'plotly_dark',
                            paper_bgcolor: 'rgba(0,0,0,0)',
                            plot_bgcolor: 'rgba(0,0,0,0)',
                            width: 800,
                            height: 450,
                            title: {
                                text: 'Amortization Schedule',
                                font: {
                                    weight: 'bold'
                                }
                            },
                            xaxis: { title: 'Payment Date' },
                            yaxis: { title: {
                                text: 'Payment Amount',
                                standoff: 20}},
                            legend: {
                                orientation: 'h',     // Horizontal orientation for the legend
                                x: 0.5,               // Center the legend
                                xanchor: 'center',    // Anchor the legend to the center
                                y: -0.3,              // Position below the plot
                                yanchor: 'bottom',     // Anchor to the bottom
                                bgcolor: 'rgba(17, 24, 39, 0.7)', // Dark background for legend
                                bordercolor: '#374151', 
                                borderwidth: 1,       
                            },
                        }}
                        config={{ responsive: true }}
                    />
                </div>
            </div>

        {/* Toggle Switch for Showing Remaining Payments */}
        <div style={{marginLeft: '40px'}}>
            <label>
                <input
                    type="checkbox"
                    checked={showRemainingOnly}
                    onChange={() => setShowRemainingOnly(!showRemainingOnly)}
                />
                Show only remaining payments
            </label>
        </div>

        <div className="data-table-container"> {/* Optional: Container for DataTable */}
            <DataTable
                title="Amortization Schedule Table"
                columns={columns}
                data={filteredSchedule} // Render the amortization schedule in the table
                pagination
                paginationPerPage={50}
                striped={false}
                highlightOnHover
                customStyles={dataTableStyles}
                conditionalRowStyles={conditionalRowStyles}
                persistTableHead
            />
        </div>
    </div>
);
};

export default AutoLoanDetail;